import { APITypesV1 } from "@cur8/api-client";
import type { Visit } from "@cur8/rich-entity";
import type { useLatestConsentsQuery } from "hooks/queries/usePatientConsentsQuery";
import { getScanFlowUrl } from "render/routes/getScanFlowUrl";
import { paths } from "render/routes/paths";
import { shouldCounterSignBeforeRoom } from "render/routes/shouldCounterSignBeforeRoom";

export type NextStepsUrlContext = {
  patientId: string;
  visitId: string;
  status: APITypesV1.VisitStatus;
  medicalExams: APITypesV1.MedicalExam[] | undefined;
  visitConsents: Visit["consents"];
  latestConsents: ReturnType<typeof useLatestConsentsQuery>["data"];
  isExtendedBloodVisit: boolean | undefined;
  hasLimsManagedBloodSamples: boolean | undefined;
  isEveryLimsBloodTubeRecorded: boolean | undefined;
  // LIMS-off equivalent of isEveryLimsBloodTubeRecorded: whether every blood
  // sample on the visit has a draw decision, for a site with the LIMS flow off.
  isEveryBloodTubeResolved: boolean | undefined;
};

type Step = (ctx: NextStepsUrlContext, next: () => string) => string;

// The pilot's blood draw is done once whichever tube-tracking mechanism the
// visit's site actually uses says so: LIMS-managed sites read the LIMS tubes,
// everyone else reads the plain draw resolution instead.
function isSangra75BloodDrawFlowCompleted(
  ctx: Pick<
    NextStepsUrlContext,
    | "hasLimsManagedBloodSamples"
    | "isEveryLimsBloodTubeRecorded"
    | "isEveryBloodTubeResolved"
  >
): boolean {
  return ctx.hasLimsManagedBloodSamples === true
    ? ctx.isEveryLimsBloodTubeRecorded === true
    : ctx.isEveryBloodTubeResolved === true;
}

/**
 * (Sangra75 pilot) The blood draw/scan intro screens need to be shown before identity check, so an extended
 * blood visit steps in front of everything else and wraps the rest of the chain behind whichever intro applies.
 * Everyone else, including a Completed/Ongoing visit (nothing left to draw, or already past this point),
 * skips straight through to identityCheckStep.
 */
const sangra75IntroScreenStep: Step = (ctx, next) => {
  if (
    !isSangra75PilotEligible(ctx) ||
    ctx.status === APITypesV1.VisitStatus.Ongoing ||
    ctx.status === APITypesV1.VisitStatus.Completed
  ) {
    return next();
  }

  const sangra75IntroPath = isSangra75BloodDrawFlowCompleted(ctx)
    ? paths.extendedVisitScanIntro
    : paths.extendedVisitBloodDrawIntro;

  return `${sangra75IntroPath.url(ctx)}?returnPath=${encodeURIComponent(next())}`;
};

/**
 * A different device/operator may pick a visit back up, so identity always gets reconfirmed here first.
 */
const identityCheckStep: Step = (ctx, next) => {
  return `${paths.identityCheck.url(ctx)}?returnPath=${encodeURIComponent(next())}`;
};

/**
 * A different device/operator may pick a visit back up, so risk gets reconfirmed on every resume, except:
 * - when the visit is completed (nothing left to ask, and re-asking would silently overwrite the answers already provided)
 * - (Sangra75 pilot) once blood draw is completed (risk was already assessed before the draw itself).
 * We also always chain the next step URL to ensure the flow continues correctly.
 */
const riskAssessmentStep: Step = (ctx, next) => {
  if (ctx.status === APITypesV1.VisitStatus.Completed) {
    return next();
  }

  if (isSangra75PilotEligible(ctx) && isSangra75BloodDrawFlowCompleted(ctx)) {
    return next();
  }

  return `${paths.immediateRiskAssessment.url(ctx)}?returnPath=${encodeURIComponent(next())}`;
};

/**
 * A visit not yet in a room may still need its study consent countersigned before room assignment.
 * - Ongoing/Completed already passed that gate when first assigned, so it's not re-checked for them.
 * - Always re-evaluated regardless of blood-draw state: a member can self-sign the study consent on
 *   their phone at any point (including while waiting on the pilot's blood draw), so a recorded draw
 *   is not proof counter-sign was already handled.
 */
const counterSignStep: Step = (ctx, next) => {
  if (
    ctx.status === APITypesV1.VisitStatus.Ongoing ||
    ctx.status === APITypesV1.VisitStatus.Completed
  ) {
    return next();
  }

  const needsCounterSign = shouldCounterSignBeforeRoom({
    visitConsents: ctx.visitConsents,
    latestConsents: ctx.latestConsents,
  });

  return needsCounterSign
    ? `${paths.counterSign.url(ctx)}?returnPath=${encodeURIComponent(next())}`
    : next();
};

/**
 * Where a pilot-eligible visit lands once identity/risk/counter-sign are behind it: room assignment once the
 * draw is done, otherwise whichever draw-tracking screen applies to the visit's site (LIMS-managed confirmation,
 * or the plain outro for a site with the LIMS flow off). A non-extended visit always goes straight to room
 * assignment, the same as a regular visit.
 *
 * Shared with ImmediateRiskAssessmentView and StudyCounterSignView, which land here too when their own step
 * is done, so the draw-completion check only has to be right in one place.
 */
export function getSangra75PilotTerminalUrl(
  ctx: Pick<
    NextStepsUrlContext,
    | "patientId"
    | "visitId"
    | "isExtendedBloodVisit"
    | "hasLimsManagedBloodSamples"
    | "isEveryLimsBloodTubeRecorded"
    | "isEveryBloodTubeResolved"
  >
): string {
  if (isSangra75BloodDrawFlowCompleted(ctx)) {
    return paths.assignToRoom.url(ctx);
  }

  return ctx.hasLimsManagedBloodSamples === true
    ? paths.extendedVisitLimsBloodDrawConfirm.url(ctx)
    : paths.extendedVisitBloodDrawOutro.url(ctx);
}

/**
 * Terminal step after risk assessment and study counter-sign that determines the final URL based on the visit context.
 * - If the visit is completed, directs to the check-out page.
 * - If the visit is ongoing, directs to the scan flow.
 * - Otherwise, directs to the pilot-aware terminal URL (see getSangra75PilotTerminalUrl).
 */
const terminalStep: Step = (ctx) => {
  if (ctx.status === APITypesV1.VisitStatus.Completed) {
    return paths.checkOut.url(ctx);
  }

  if (ctx.status === APITypesV1.VisitStatus.Ongoing) {
    return getScanFlowUrl(ctx);
  }

  if (isSangra75PilotEligible(ctx)) {
    return getSangra75PilotTerminalUrl(ctx);
  }

  return paths.assignToRoom.url(ctx);
};

/**
 * Checks the appropriate next step URL based on the visit context: the sangra75 intro screen (when it applies),
 * then identity check, then risk assessment, counter-sign, and finally the terminal destination.
 */
export const nextStepsUrl = compose(
  sangra75IntroScreenStep,
  identityCheckStep,
  riskAssessmentStep,
  counterSignStep,
  terminalStep
);

/**
 * Composes multiple steps into a single flow function - each step receives the context and a `next` function to call the subsequent step or to terminate the flow.
 * @param steps The sequence of steps to compose into a single flow.
 * @returns A function that takes the context and returns the URL of the next step.
 */
function compose(...steps: Step[]): (ctx: NextStepsUrlContext) => string {
  return (ctx) => {
    let index = 0;
    function next(): string {
      return steps[index++](ctx, next);
    }
    return next();
  };
}

function isSangra75PilotEligible(
  ctx: Pick<NextStepsUrlContext, "isExtendedBloodVisit">
): boolean {
  return ctx.isExtendedBloodVisit === true;
}
